import { useEffect, useState } from 'react';
import { Flex, Button, Heading, Box, Step, StepDescription, StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle, Stepper, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import Loader from '../Loader/Loader';
import { AiFillCheckCircle } from "react-icons/ai";

export interface WizardStep {
  label: string;
  description?: string;
  content: JSX.Element;
  requiresValidation?: boolean;
  isValidated?: boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  finishStep?: JSX.Element;
  showButtons?: boolean;
  onSubmit?: () => void;
  loading?: boolean;
  showStepper?: boolean;
  onStepChange?: (activeStep: number) => void;
  initialStep?: number;
  minStep?: number;
}

export default function Wizard({ steps, finishStep, showButtons = true, onSubmit = () => { }, loading = false, showStepper = true, onStepChange, initialStep, minStep }: WizardProps) {
  const totalSteps = steps.length;
  const maxStepIndex = Math.max(totalSteps - 1, 0);
  const effectiveMinStep = Math.min(Math.max(minStep ?? 0, 0), maxStepIndex);
  const [activeStep, setActiveStep] = useState(() => {
    const startingStep = initialStep ?? 0;
    return Math.min(Math.max(startingStep, effectiveMinStep), maxStepIndex);
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const orientation = isMobile ? 'vertical' : 'horizontal';
  const isLastStep = activeStep === maxStepIndex;
  const hasCompletedAllSteps = activeStep === totalSteps;
  const buttonBg = useColorModeValue('white', 'gray.700');
  const buttonHoverBg = useColorModeValue('gray.50', 'gray.600');
  const buttonColor = useColorModeValue('gray.800', 'white');

  const isNextButtonDisabled = () => {
    const currentStep = steps[activeStep];
    return currentStep.requiresValidation && !currentStep.isValidated;
  };

  useEffect(() => {
    setActiveStep((previous) => {
      const clamped = Math.min(Math.max(previous, effectiveMinStep), maxStepIndex);
      if (clamped !== previous) {
        onStepChange?.(clamped);
      }
      return clamped;
    });
  }, [totalSteps, maxStepIndex, effectiveMinStep, onStepChange]);

  useEffect(() => {
    if (initialStep === undefined) {
      return;
    }
    setActiveStep((previous) => {
      const desired = Math.min(Math.max(initialStep, effectiveMinStep), maxStepIndex);
      if (desired !== previous) {
        onStepChange?.(desired);
      }
      return desired;
    });
  }, [initialStep, effectiveMinStep, maxStepIndex, onStepChange]);

  const goToNextStep = () => {
    const nextStep = Math.min(activeStep + 1, maxStepIndex);
    setActiveStep(nextStep);
    onStepChange?.(nextStep);
  };

  const goToPreviousStep = () => {
    if (activeStep <= effectiveMinStep) {
      return;
    }
    const nextStep = Math.max(activeStep - 1, effectiveMinStep);
    if (nextStep !== activeStep) {
      setActiveStep(nextStep);
      onStepChange?.(nextStep);
    }
  };

  function renderFinishingStep() {
    if (finishStep) {
      return finishStep;
    } else {
      return (
        <Box sx={{ my: 8, p: 8, rounded: "md" }}>
          <Heading fontSize="xl" textAlign="center">
            All steps completed!
          </Heading>
        </Box>
      );
    }
  }

  function renderStep(content: JSX.Element) {
    if (loading) {
      return (
        <Flex justifyContent={'center'} marginTop={4} marginBottom={4}>
          <Loader />
        </Flex>
      );
    } else {
      return (
        <Box rounded={'md'} marginTop={4} marginBottom={4}>
          {content}
        </Box>
      );
    }
  }

  function renderButtonContainer() {
    if (showButtons) {
      return (
        <Box position="sticky" bottom={4} mt={4} alignSelf="center" zIndex={1}>
          <Flex justify="center" align="center" gap={8} px={2}>
            <Button
              isDisabled={activeStep <= effectiveMinStep}
              onClick={goToPreviousStep}
              size="sm"
              variant="solid"
              bg={buttonBg}
              color={buttonColor}
              boxShadow="lg"
              px={6}
              py={5}
              _hover={{ bg: buttonHoverBg }}
              _disabled={{ opacity: 0.6, cursor: 'not-allowed', bg: buttonBg, color: buttonColor }}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="solid"
              bg={buttonBg}
              color={buttonColor}
              boxShadow="lg"
              px={6}
              py={5}
              onClick={isLastStep ? onSubmit : goToNextStep}
              isDisabled={isNextButtonDisabled()}
              _hover={{ bg: buttonHoverBg }}
              _disabled={{ opacity: 0.6, cursor: 'not-allowed', bg: buttonBg, color: buttonColor }}
            >
              {isLastStep ? "Finish" : "Next"}
            </Button>
          </Flex>
        </Box>
      );
    }
  }

  function renderStepper() {
    if (showStepper && steps.length > 1) {
      return (
        <Stepper index={activeStep} orientation={orientation}>
          {steps.map((step, index) => (
            <Step key={index} justifyContent={'center'} alignItems={'center'}>
              <StepIndicator >
                <StepStatus
                  complete={<StepIcon />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>
              <Flex flexShrink='0' flexDirection={'column'}>
                <StepTitle
                  fontSize="sm"
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {step.label}
                </StepTitle>
                <StepDescription
                  fontSize="xs"
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {step.description}
                </StepDescription>
              </Flex>
              <StepSeparator />
            </Step>
          ))}
        </Stepper>
      );
    }
  }

  return (
    <Flex flexDirection={'column'} width="100%">
      {renderStepper()}
      {renderStep(steps[activeStep].content)}
      {hasCompletedAllSteps ? renderFinishingStep() : renderButtonContainer()}
    </Flex>
  );
}
